import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Video, Phone, Check, CalendarDays, MessageCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

export const BRAND_WHATSAPP = '917621003708';

const PRICE_RANGES = ['Up to ₹5,000', '₹5,000 – ₹10,000', '₹10,000 – ₹25,000', 'Above ₹25,000'];

const dayList = (count = 14) => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
    out.push({
      iso: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      dow: d.toLocaleDateString('en-IN', { weekday: 'short' }),
      day: d.getDate(),
      month: d.toLocaleDateString('en-IN', { month: 'short' })
    });
  }
  return out;
};

const BookCall = () => {
  const days = dayList(14);
  const [date, setDate] = useState(days[0].iso);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slot, setSlot] = useState('');
  const [callType, setCallType] = useState('video');
  const [interestType, setInterestType] = useState('item');
  const [form, setForm] = useState({ full_name: '', phone: '', interest_item: '', price_range: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoadingSlots(true);
      setSlot('');
      try {
        const res = await api.get('/appointments/slots', { params: { date } });
        setSlots(res.data.slots);
      } catch {
        setSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };
    load();
  }, [date]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) { toast.error('Please add your name and mobile number'); return; }
    if (!slot) { toast.error('Please pick a time slot'); return; }
    if (interestType === 'item' && !form.interest_item.trim()) { toast.error('Tell us which item interests you'); return; }
    if (interestType === 'price_range' && !form.price_range) { toast.error('Please choose a price range'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/appointments', {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        call_type: callType,
        date,
        slot,
        interest_type: interestType,
        interest_item: interestType === 'item' ? form.interest_item.trim() : null,
        price_range: interestType === 'price_range' ? form.price_range : null,
        note: form.note.trim() || null
      });
      setBooked(res.data);
      toast.success('Request sent — we will confirm your time shortly');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send your request');
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappNotify = (appt) => {
    const lines = [
      'Hello Bandhani Heritage, I have requested an appointment:',
      `Name: ${appt.full_name}`,
      `Mobile: ${appt.phone}`,
      `Type: ${appt.call_type === 'video' ? 'Video call' : 'Phone call'}`,
      `Date & time: ${appt.date} at ${appt.slot} IST`,
      appt.interest_item ? `Interested in: ${appt.interest_item}` : `Budget: ${appt.price_range}`,
    ];
    if (appt.note) lines.push(`Note: ${appt.note}`);
    return `https://wa.me/${BRAND_WHATSAPP}?text=${encodeURIComponent(lines.join('\n'))}`;
  };

  if (booked) {
    return (
      <div className="py-24 md:py-32 max-w-2xl mx-auto px-4" data-testid="booking-success">
        <div className="border border-border rounded-2xl p-8 md:p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-6">
            <Check className="h-7 w-7" />
          </div>
          <h1 className="text-3xl md:text-4xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Request received
          </h1>
          <p className="text-sm text-muted-foreground mt-3">
            {booked.call_type === 'video' ? 'Video call' : 'Call'} requested for{' '}
            <span className="text-foreground font-medium">
              {new Date(`${booked.date}T${booked.slot}`).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })} at {booked.slot} IST
            </span>
            . We will confirm this time or suggest a nearby slot.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
            <a href={whatsappNotify(booked)} target="_blank" rel="noopener noreferrer" data-testid="booking-whatsapp-notify">
              <Button className="rounded-full w-full sm:w-auto bg-[#25D366] hover:bg-[#1da851] text-white">
                <MessageCircle className="h-4 w-4 mr-2" /> Ping us on WhatsApp
              </Button>
            </a>
            <Link to={`/my-bookings?phone=${encodeURIComponent(booked.phone)}`}>
              <Button variant="outline" className="rounded-full w-full sm:w-auto" data-testid="booking-view-status">
                View booking status
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32 max-w-3xl mx-auto px-4 md:px-8" data-testid="book-call-page">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Personal Appointment</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Book a Call
        </h1>
        <p className="text-base text-muted-foreground mt-4 max-w-xl">
          See sarees live on a video call, ask about fabric, colours and prices. Pick a time that suits you — we
          confirm every appointment personally.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-8">
        {/* Call type */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">How would you like to connect?</Label>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {[
              { id: 'video', label: 'Video call', icon: Video, hint: 'See the pieces live' },
              { id: 'call', label: 'Phone call', icon: Phone, hint: 'Just talk it through' }
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setCallType(opt.id)}
                className={`text-left border rounded-xl p-4 transition-colors ${callType === opt.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                data-testid={`call-type-${opt.id}`}
              >
                <opt.icon className={`h-5 w-5 mb-2 ${callType === opt.id ? 'text-primary' : 'text-muted-foreground'}`} />
                <p className="font-medium text-sm">{opt.label}</p>
                <p className="text-xs text-muted-foreground">{opt.hint}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Date */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <CalendarDays className="h-3.5 w-3.5" /> Pick a date
          </Label>
          <div className="flex gap-2 overflow-x-auto pb-2 mt-3" data-testid="date-strip">
            {days.map((d) => (
              <button
                key={d.iso}
                type="button"
                onClick={() => setDate(d.iso)}
                className={`flex-shrink-0 w-16 py-3 rounded-xl border text-center transition-colors ${date === d.iso ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:border-primary/40'}`}
                data-testid={`date-${d.iso}`}
              >
                <span className="block text-[10px] uppercase tracking-widest opacity-70">{d.dow}</span>
                <span className="block text-lg font-light leading-tight">{d.day}</span>
                <span className="block text-[10px] uppercase opacity-70">{d.month}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Slots */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            <Clock className="h-3.5 w-3.5" /> Pick a time (IST)
          </Label>
          {loadingSlots ? (
            <p className="text-sm text-muted-foreground mt-3 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading slots…</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3" data-testid="slot-grid">
              {slots.map((s) => (
                <button
                  key={s.slot}
                  type="button"
                  disabled={!s.available}
                  onClick={() => setSlot(s.slot)}
                  className={`py-2 rounded-full border text-sm transition-colors ${slot === s.slot ? 'border-primary bg-primary text-primary-foreground' : s.available ? 'border-border hover:border-primary/40' : 'border-dashed border-border text-muted-foreground/40 cursor-not-allowed line-through'}`}
                  data-testid={`slot-${s.slot}`}
                >
                  {s.slot}
                </button>
              ))}
              {slots.length === 0 && <p className="text-sm text-muted-foreground col-span-full">No slots for this date.</p>}
            </div>
          )}
          {!loadingSlots && slots.length > 0 && slots.every((s) => !s.available) && (
            <p className="text-sm text-muted-foreground mt-3" data-testid="no-slots-hint">
              All slots for this date are taken or already past — please choose another date.
            </p>
          )}
        </div>

        {/* Interest */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">What are you looking for?</Label>
          <div className="flex gap-2 mt-3">
            <Button type="button" variant={interestType === 'item' ? 'default' : 'outline'} className="rounded-full" onClick={() => setInterestType('item')} data-testid="interest-item-btn">
              A particular item
            </Button>
            <Button type="button" variant={interestType === 'price_range' ? 'default' : 'outline'} className="rounded-full" onClick={() => setInterestType('price_range')} data-testid="interest-price-btn">
              A price range
            </Button>
          </div>
          {interestType === 'item' ? (
            <Input
              value={form.interest_item}
              onChange={set('interest_item')}
              placeholder="e.g. Red Gharchola saree, Ajrakh dupatta"
              className="rounded-full mt-3"
              data-testid="interest-item-input"
            />
          ) : (
            <Select value={form.price_range} onValueChange={(v) => setForm((f) => ({ ...f, price_range: v }))}>
              <SelectTrigger className="rounded-full mt-3" data-testid="price-range-select"><SelectValue placeholder="Select a budget" /></SelectTrigger>
              <SelectContent>
                {PRICE_RANGES.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Contact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Full name</Label>
            <Input value={form.full_name} onChange={set('full_name')} className="rounded-full mt-2" data-testid="booking-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mobile number</Label>
            <Input value={form.phone} onChange={set('phone')} placeholder="WhatsApp preferred" className="rounded-full mt-2" data-testid="booking-phone-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Anything else? (optional)</Label>
          <Textarea value={form.note} onChange={set('note')} rows={3} className="rounded-xl mt-2" data-testid="booking-note-input" />
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" className="rounded-full px-8" disabled={submitting} data-testid="booking-submit-btn">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : 'Request appointment'}
          </Button>
          <Link to="/my-bookings" className="text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="link-my-bookings">
            Already booked? Check status
          </Link>
        </div>
      </form>
    </div>
  );
};

export default BookCall;
